import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    NgModel,
    UntypedFormControl,
    ValidationErrors,
    Validator,
} from '@angular/forms';

import { CommonMessages, DeviceService, TimerService } from '@frontend/vanilla/core';
import { DateConfig, FormFieldComponent } from '@frontend/vanilla/shared/forms';
import { Subject } from 'rxjs';

import { NumberAllowedOnlyDirective } from '../../directives/number.directive';
import { CommonMethodsService } from '../../tracking/common-methods-service';

/**
 * @stable
 */
export class DateModel {
    day: any | null;
    month: any | null;
    year: any | null;
}

/**
 * @whatItDoes Provides day month year picker component and returns javascript Date object
 *
 * @stable
 */
@Component({
    selector: 'pt-date-v2',
    templateUrl: 'pt-date-v2.component.html',
    providers: [
        { provide: NG_VALIDATORS, useExisting: PortalDateV2Component, multi: true },
        { provide: NG_VALUE_ACCESSOR, useExisting: PortalDateV2Component, multi: true },
    ],
    imports: [NgClass, FormFieldComponent, FormsModule, NgTemplateOutlet, NumberAllowedOnlyDirective],
})
export class PortalDateV2Component implements OnInit, ControlValueAccessor, Validator {
    @Input() required: boolean;
    /** Toggle for Entertainment Registration UI changes. Applicable only for registration area */
    @Input() isEntertainmentRegistration: boolean = false;
    /** Specifies callback when date changes */
    @Output() onDateChanged: EventEmitter<Date | null> = new EventEmitter();
    /** Specifies callback when day changes */
    @Output() onDayChanged: EventEmitter<any> = new EventEmitter();
    /** Specifies callback when month changes */
    @Output() onMonthChanged: EventEmitter<any> = new EventEmitter();
    /** Specifies callback when year changes */
    @Output() onYearChanged: EventEmitter<any> = new EventEmitter();
    @Output() dobChanged: EventEmitter<any> = new EventEmitter();
    /** Specifies if autocomplete is enabled */
    @Input() autocomplete: boolean;
    /** context of the registration */
    @Input() areaName: boolean;
    @Input() subAreaName: boolean;
    @Input() olderThan?: number;
    @Input() youngerThan?: number;
    @Input() adjustFocus: Subject<any>;
    @Input() fromUkgc: boolean;
    @Input() context?: any;
    /** Specifies if scroll is need for conversation registration */
    @Output() focusFieldEventCompleted: EventEmitter<boolean> = new EventEmitter();

    @ViewChild('day') dayElement: NgModel;
    @ViewChild('month') monthElement: NgModel;
    @ViewChild('year') yearElement: NgModel;

    isDisabled: boolean;
    dayPlaceholder: string;
    monthPlaceholder: string;
    yearPlaceholder: string;
    model: DateModel = { day: null, month: null, year: null };
    days: number[] = [];
    daysInMonth: any;
    ready: boolean;
    showMonthFirst: boolean = false;
    private control: UntypedFormControl;
    private preFilled: boolean;
    private dayElementName: string = 'day';
    private monthElementName: string = 'month';
    private yearElementName: string = 'year';
    private requiredErrorMessage: string = 'ErrorInMultipleFields';
    constructor(
        private commonMessages: CommonMessages,
        private elementRef: ElementRef,
        private deviceService: DeviceService,
        private timerService: TimerService,
        public dateConfig: DateConfig,
        private commonService: CommonMethodsService,
    ) {}

    onChange: (value: any) => void = () => {};
    onTouched = () => {};

    async ngOnInit() {
        this.context?.events?.setValidationForDOB?.subscribe((data: any) => {
            if (data) {
                this.yearElement.control.setErrors({ incorrect: true });
                this.yearElement.control.markAsTouched();
                this.monthElement.control.setErrors({ incorrect: true });
                this.monthElement.control.markAsTouched();
                this.dayElement.control.setErrors({ incorrect: true });
                this.dayElement.control.markAsTouched();
            }
        });
        if (!this.dateConfig.isConfigReady) await this.commonService.loadClientConfig([DateConfig]);
        this.dayPlaceholder = this.commonMessages['DateDayPlaceholder'];
        this.monthPlaceholder = this.commonMessages['DateMonthPlaceholder'];
        this.yearPlaceholder = this.commonMessages['DateYearPlaceholder'];
        this.adjustFocus?.subscribe((data) => this.adjustFocusOnClick(data));
        this.showMonthFirst = this.dateConfig.showMonthFirst;
    }
    /*forcefully set component invalid based on server data or validation errors*/
    setComponentInvalid(errorType: string = 'serverError') {
        this.yearElement.control.setErrors({ [errorType]: true });
        this.yearElement.control.markAsTouched();
        this.monthElement.control.setErrors({ [errorType]: true });
        this.monthElement.control.markAsTouched();
        this.dayElement.control.setErrors({ [errorType]: true });
        this.dayElement.control.markAsTouched();
    }
    validate(c: UntypedFormControl): ValidationErrors | null {
        this.control = c;
        const date = new Date();
        // set default amount of days relying on first month of year
        const defAmountOfDays = new Date(date.getFullYear(), 0).getDate();
        const year = this.model.year ? parseInt(this.model.year) : date.getFullYear();
        const month = this.model.month ? this.model.month : defAmountOfDays;

        this.daysInMonth = new Date(year, month, 0).getDate();

        //Default to 31 days for day value range if month is invalid.
        if (this.model.month && (this.model.month < 1 || this.model.month > 12)) {
            this.daysInMonth = 31;
        }
        this.days = this.range(1, this.daysInMonth);
        const errors = {};
        this.validateDayElement(errors);
        this.validateMonthElement(errors);
        this.validateYearElement(errors);
        if (Object.keys(errors).length == 0) return null;
        return Object.keys(errors).length > 1 ? { [this.requiredErrorMessage]: true } : errors;
    }
    validateYearElement(errors: { [key: string]: boolean }) {
        let maxAgeYear = 0;
        let minAgeYear = new Date().getFullYear(); //default min can be anything.
        if (this.olderThan) maxAgeYear = new Date().getFullYear() - this.olderThan;
        if (this.youngerThan) minAgeYear = new Date().getFullYear() - this.youngerThan;
        /**This is for year >= 1000 scenario bcoz we are pushing date nly if year>=1000 (constructing date with 2 digits, gives wrong info in new Date()) */
        if (this.control.errors && (this.control.errors.youngerthan || this.control.errors.olderthan)) {
            this.yearElement.control.setErrors({ required: true });
            this.yearElement.control.markAsTouched();
        } else if (this.model.year && parseInt(this.model.year) < maxAgeYear) {
            this.yearElement.control.setErrors({ required: true });
            errors['invalidyearpart'] = true;
        } else if (this.model.year && parseInt(this.model.year) > minAgeYear) {
            this.yearElement.control.setErrors({ required: true });
            errors['invalidyearpart'] = true;
        } else if ((this.yearElement?.touched || this.yearElement?.dirty) && !this.model.year) {
            this.yearElement.control.setErrors({ required: true });
            errors['requiredyear'] = true;
        } else {
            this.yearElement?.control.setErrors(null);
        }
    }
    validateMonthElement(errors: any) {
        if (this.control.errors && this.control.errors.youngerthan) {
            this.monthElement.control.setErrors({ required: true });
            this.monthElement.control.markAsTouched();
        } else if (this.model.month && (this.model.month < 1 || this.model.month > 12)) {
            this.monthElement.control.setErrors({ required: true });
            errors['invalidmonthpart'] = true;
        } else if ((this.monthElement?.touched || this.monthElement?.dirty) && !this.model.month) {
            this.monthElement.control.setErrors({ required: true });
            errors['requiredmonth'] = true;
        } else {
            this.monthElement?.control.setErrors(null);
        }
    }
    validateDayElement(errors: any) {
        if (this.control.errors && this.control.errors.youngerthan) {
            this.dayElement.control.setErrors({ required: true });
            this.dayElement.control.markAsTouched();
        } else if (this.model.day && this.days.indexOf(parseInt(this.model.day)) == -1) {
            this.dayElement.control.setErrors({ required: true });
            if (this.days.length === 31) {
                errors['invaliddatepart'] = true;
            } else if (this.days.length === 30) {
                errors['invaliddatepart30days'] = true;
            } else if (this.days.length === 29) {
                errors['invaliddatepart29days'] = true;
            } else if (this.days.length === 28) {
                errors['invaliddatepart28days'] = true;
            }
        } else if ((this.dayElement?.touched || this.dayElement?.dirty) && !this.model.day) {
            //hack for the first time,press tab,not showing error..it shows only after changedetection runs
            if (this.dayElement?.pristine) setTimeout(() => this.dayElement.control.setErrors({ required: true }), 100);
            else this.dayElement.control.setErrors({ required: true });
            errors['requiredday'] = true;
        } else {
            this.dayElement?.control.setErrors(null);
        }
    }
    writeValue(obj: Date | string): void {
        if (obj) {
            obj = this.normalizedDate(obj);
            this.model.year = obj.getFullYear().toString();
            this.model.month = obj.getMonth() + 1;
            this.model.day = obj.getDate();
            this.preFilled = true;
        }
    }
    normalizedDate(date: string | Date): Date {
        if (typeof date === 'string') {
            const dateArray = date.split('-');
            const year = dateArray[0];
            const month = dateArray[1];
            const day = dateArray[2];
            const mydate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0, 0);
            mydate.setFullYear(parseInt(year));
            return mydate;
        }
        return date;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    range(start: number, end: number): number[] {
        const result = [];
        for (let i = start; i <= end; i++) {
            result.push(i);
        }
        return result;
    }

    getDate(): Date | null {
        if (this.model.year && this.model.month && this.model.day && parseInt(this.model.year) >= 1000) {
            const date = new Date(parseInt(this.model.year), this.model.month - 1, this.model.day, 12, 0, 0, 0);
            //Confirm that the values returned from the generated date are the same as passed, since new Date() never throws errors for dates like 31/02.
            if (date.getFullYear().toString() == this.model.year && date.getMonth() == this.model.month - 1 && date.getDate() == this.model.day) {
                return date;
            }
        }
        return null;
    }

    onModelChange(event: number, element: string) {
        this.model = { ...this.model, [element]: event };
        this.moveToNext(element);
        if (this.onDateChanged.observers.length) {
            const selectedDate = this.getDate();
            this.onDateChanged.emit(selectedDate);
        }
        this.removeErrorIfEmpty(element);
        this.dobChanged.emit(this.model.year + '-' + this.model.month + '-' + this.model.day);
    }

    onDayChange(day: Event) {
        const element = this.elementRef.nativeElement.querySelector('input[name="day"]');
        if (element.value.length === 1) {
            element.value = '0' + element.value;
        }
        const selectedDate = this.getDate();
        //hack for the first time prefilled,press tab,not showing error..it shows only after changedetection runs
        if (this.preFilled) setTimeout(() => this.control.updateValueAndValidity({ onlySelf: true }), 100);
        this.onChange(selectedDate);
        this.control.updateValueAndValidity({ onlySelf: true });
        this.onTouched();
        if (this.onDayChanged.observers.length > 0) this.onDayChanged.emit(day);
    }

    onMonthChange(month: Event) {
        const element = this.elementRef.nativeElement.querySelector('input[name="month"]');
        if (element.value.length === 1) {
            element.value = '0' + element.value;
        }
        const selectedDate = this.getDate();
        this.onChange(selectedDate);
        this.control.updateValueAndValidity({ onlySelf: true });
        this.onTouched();
        if (this.onMonthChanged.observers.length > 0) this.onMonthChanged.emit(month);
    }

    onYearChange(year: Event) {
        const selectedDate = this.getDate();
        this.onChange(selectedDate);
        this.control.updateValueAndValidity({ onlySelf: true });
        this.onTouched();
        if (this.onYearChanged.observers.length > 0) this.onYearChanged.emit(year);
    }

    moveToNext(element: string) {
        const currentElement = this.elementRef.nativeElement.querySelector('input[name="' + element + '"]');
        const nextElement = this.getNextElement(element);
        if (currentElement.value.length >= currentElement.maxLength) {
            if (nextElement) this.focusElement(nextElement, false);
            currentElement.blur();
        }
    }

    getNextElement(element: string) {
        if (element === this.dayElementName) {
            return this.dateConfig.showMonthFirst ? this.yearElementName : this.monthElementName;
        } else if (element === this.monthElementName) {
            return this.dateConfig.showMonthFirst ? this.dayElementName : this.yearElementName;
        } else return null;
    }
    onFieldFocus(element: string) {
        if (this.areaName && this.subAreaName && this.deviceService.isMobilePhone) {
            this.focusFieldEventCompleted.emit(true);
        }
        this.removeErrorIfEmpty(element);
    }
    adjustFocusOnClick(isSubmit: boolean) {
        //this is to handle on next/submit click, validate the component and focus the correct field.
        if (isSubmit) {
            this.dayElement.control.markAsTouched();
            this.monthElement.control.markAsTouched();
            this.yearElement.control.markAsTouched();
        }
        if (this.dateConfig.showMonthFirst) {
            if (!this.model.month || this.monthElement.control.errors) this.focusElement(this.monthElementName);
            else if (!this.model.day || this.dayElement.control.errors) this.focusElement(this.dayElementName);
            else this.focusElement(this.yearElementName);
        } else {
            if (!this.model.day || this.dayElement.control.errors) {
                this.focusElement(this.dayElementName);
            } else if (!this.model.month || this.monthElement.control.errors) this.focusElement(this.monthElementName);
            else this.focusElement(this.yearElementName);
        }
    }
    focusElement(element: string, delayFocus: boolean = true) {
        if (delayFocus) {
            //this is required bcoz after clicking next blur and focus events firing immediately.
            this.timerService.setTimeoutOutsideAngularZone(() => {
                this.elementRef.nativeElement.querySelector('input[name="' + element + '"]').focus();
            }, 200);
        } else {
            this.elementRef.nativeElement.querySelector('input[name="' + element + '"]').focus();
        }
    }
    private removeErrorIfEmpty(element: string) {
        const errors = {};
        if (!this.control.errors) return;
        if (element === this.dayElementName) {
            if (!this.model.day) {
                this.control.setErrors(null);
                this.validateMonthElement(errors);
                this.validateYearElement(errors);
            }
        } else if (element === this.monthElementName) {
            if (!this.model.month) {
                this.control.setErrors(null);
                this.validateDayElement(errors);
                this.validateYearElement(errors);
            }
        } else {
            if (!this.model.year) {
                this.control.setErrors(null);
                this.validateDayElement(errors);
                this.validateMonthElement(errors);
            }
        }
        if (Object.keys(errors).length > 0) this.control.setErrors(Object.keys(errors).length > 1 ? { [this.requiredErrorMessage]: true } : errors);
    }
}
